import React, { useState, useEffect, useCallback } from 'react';
import { Col, Row, Form, Button } from 'react-bootstrap';
import { Card, CardBody, CardTitle, CardTitleWrap, CardSubhead } from '@/shared/components/Card';
import { getDisponibilidadeColumns } from './columnsDisponibilidade';
import ReactTableBase from '@/shared/components/table/ReactTableBase';
import ReactTableCustomizer from '@/shared/components/table/components/ReactTableCustomizer';
import TableHeaderButton from '@/shared/components/table/components/ReactHeaderButton';
import ReactTablePagination from '@/shared/components/table/components/ReactTablePagination';
import styled from 'styled-components';
import Filtros from './Filtros';
import { post } from '@/utils/api/base';

const ExampleCard = () => {
  const [rows, setRows] = useState([]);
  const [columns, setColumns] = useState([]);
  const [isSortable, setIsSortable] = useState(false);
  const [withPagination, setWithPagination] = useState(false);
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [totalRegistros, setTotalRegistros] = useState(0);
  const [withSearchEngine, setWithSearchEngine] = useState(() => {
    const saved = localStorage.getItem('filterMode');
    return saved === 'true';
  });

  const [filtros, setFiltros] = useState({
    LocalCidade: '',
    LocalEstado: '',
    Frota: '',
    Veiculo: '',
    NomeGestor: '',
    NomeComercial: '',
    Destino: '',
    Vazio: '',
    Coleta: 'false',
    Negociacao: '',
  });

  const handleClickWithSearchEngine = () => {
    const newValue = !withSearchEngine;
    setWithSearchEngine(newValue);
    localStorage.setItem('filterMode', newValue.toString());
  };

  const handleFiltroChange = (e) => {
    const { name, value } = e.target;
    setFiltros((prev) => ({ ...prev, [name]: value }));
  };

  const handleLimparFiltros = () => {
    setFiltros({
      LocalCidade: '',
      LocalEstado: '',
      Frota: '',
      Veiculo: '',
      NomeGestor: '',
      NomeComercial: '',
      Destino: '',
      Vazio: '',
      Coleta: 'false',
      Negociacao: '',
    });
    setPageIndex(0);
  };

  const fetchData = useCallback(async () => {
    try {
      const payload = {
        quantidadePorPagina: pageSize,
        pagina: pageIndex + 1,
        LocalCidade: filtros.LocalCidade || undefined,
        LocalEstado: filtros.LocalEstado || undefined,
        Frota: filtros.Frota || undefined,
        Veiculo: filtros.Veiculo || undefined,
        NomeGestor: filtros.NomeGestor || undefined,
        NomeComercial: filtros.NomeComercial || undefined,
        Destino: filtros.Destino || undefined,
        Vazio: filtros.Vazio === '' ? undefined : filtros.Vazio === 'true',
        Coleta: filtros.Coleta === '' ? undefined : filtros.Coleta === 'true',
        Negociacao: filtros.Negociacao === '' ? undefined : filtros.Negociacao === 'true',
      };

      const response = await post(`${process.env.REACT_APP_BASE_API_URL}/frotas/disponibilidade`, payload);
      const data = response.data;

      // Função para calcular dias parados
      const calcularDiasParados = (dataDisponibilidade) => {
        // Obter data atual
        const dataAtual = new Date();
        
        // Converter a data da disponibilidade para objeto Date
        const dataDisponibilidadeObj = new Date(dataDisponibilidade);
        
        // Calcular diferença em milissegundos
        const diferencaMs = dataAtual.getTime() - dataDisponibilidadeObj.getTime();
        
        // Converter para dias (arredondar para baixo)
        const diferencaDias = Math.floor(diferencaMs / (1000 * 60 * 60 * 24));
        
        // Retorna 0 se a data for futura, caso contrário retorna a diferença
        return Math.max(0, diferencaDias);
      };

      const mappedData = data.registros.map((item) => {
        const diasParados = calcularDiasParados(item.DataDisponibilidade);
        
        return {
        id: item.Id,
        data: new Date(item.DataDisponibilidade).toLocaleDateString('pt-BR'),
          dataDisponibilidade: item.DataDisponibilidade,
        vazioem: item.LocalCidade,
        estado: item.LocalEstado,
        frota: item.Frota,
        date: new Date(item.DataCriacao).toLocaleString('pt-BR'),
        tipoveiculo: item.Veiculo,
        gestor: item.NomeGestor,
        vazio: item.Vazio === 1 ? 'Sim' : 'Não',
        coleta: item.Coleta === 1 ? 'Sim' : 'Não',
        negociacao: item.Negociacao === 1 ? 'Sim' : 'Não',
        comercial: item.NomeComercial,
        destino: item.Destino,
          disparados: diasParados,
        };
      });

      setRows(mappedData);
      setTotalRegistros(data.totalRegistros);
      setColumns(getDisponibilidadeColumns());
    } catch (error) {
      console.error('Erro ao buscar dados:', error);
      setRows([]);
      setColumns([]);
    }
  }, [pageIndex, pageSize, filtros]);

  // Função para pesquisa que reseta a página para 1
  const handlePesquisar = useCallback(() => {
    setPageIndex(0); // Reset para página 1
    // fetchData será chamado automaticamente pelo useEffect
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleRowClick = (row) => {
    // Recalcular dias parados com a data atual
    const calcularDiasParados = (dataDisponibilidade) => {
      const dataAtual = new Date();
      const dataDisponibilidadeObj = new Date(dataDisponibilidade);
      const diferencaMs = dataAtual.getTime() - dataDisponibilidadeObj.getTime();
      const diferencaDias = Math.floor(diferencaMs / (1000 * 60 * 60 * 24));
      return Math.max(0, diferencaDias);
    };

    // Criar uma cópia do row com os dias parados atualizados
    const rowAtualizado = {
      ...row,
      disparados: calcularDiasParados(row.dataDisponibilidade)
    };

    localStorage.setItem('disponibilidadeSelecionada', JSON.stringify(rowAtualizado));
    window.location.href = `/pages/programacao/detalhesdisponibilidade/${row.id}`;
  };

  const tableConfig = {
    isEditable: false,
    isSortable,
    isResizable: false,
    withDragAndDrop: false,
    withPagination,
    withSearchEngine,
    manualPageSize: [10, 20, 30],
    ...(withSearchEngine && { placeholder: 'Buscar...' }),
    onRowClick: handleRowClick,
  };

  return (
    <Col md={12}>
      <Card>
        <CardBody>
          <HeaderWrap>
            <HeaderWrap2>
              <CardTitleWrap>
                <CardTitle>Disponibilidade DE VEÍCULOS</CardTitle>
                <CardSubhead>Informações de disponibilidade dos veículos</CardSubhead>
              </CardTitleWrap>

              <ButtonContainer>
              <ReactTableCustomizer
                handleClickIsSortable={() => setIsSortable(!isSortable)}
                handleClickWithPagination={() => setWithPagination(!withPagination)}
                handleClickWithSearchEngine={handleClickWithSearchEngine}
                isSortable={isSortable}
                withPagination={withPagination}
                withSearchEngine={withSearchEngine}
              />

            <TableHeaderButton
              titleButton="Adicionar"
              onClick={() => window.location.href = '/pages/programacao/criardisponibilidade'}
            />
              </ButtonContainer>
            </HeaderWrap2>
          </HeaderWrap>

          {withSearchEngine && (
            <Filtros
              filtros={filtros}
              handleFiltroChange={handleFiltroChange}
              fetchData={handlePesquisar}
              handleLimparFiltros={handleLimparFiltros}
            />
          )}

          <ReactTableBase
            columns={columns}
            data={rows}
            updateDraggableData={() => { }}
            tableConfig={tableConfig}
          />

          <ReactTablePagination
            pageIndex={pageIndex}
            pageSize={pageSize}
            setPageSize={setPageSize}
            gotoPage={setPageIndex}
            previousPage={() => setPageIndex((prev) => Math.max(prev - 1, 0))}
            nextPage={() => setPageIndex((prev) => prev + 1)}
            canPreviousPage={pageIndex > 0}
            canNextPage={(pageIndex + 1) * pageSize < totalRegistros}
            pageOptions={Array.from({ length: Math.ceil(totalRegistros / pageSize) }, (_, i) => i)}
            dataLength={totalRegistros}
            page={rows}
            manualPageSize={[10, 20, 30]}
          />
        </CardBody>
      </Card>
    </Col>
  );
};

export default ExampleCard;

// Styles
const HeaderWrap = styled.div`
  display: flex;
  flex-direction: column;
`;

const HeaderWrap2 = styled.div`
  display: flex;
  width: 100%;
  & > div:first-child {
    margin-right: auto;
  }

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 15px;
    
    & > div:first-child {
      margin-right: 0;
    }
  }

  @media (max-width: 576px) {
    gap: 12px;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 8px;
    align-items: stretch;
  }

  @media (max-width: 576px) {
    gap: 6px;
  }
`;

const LegendaContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  margin-bottom: 15px;
  padding: 10px;
  background-color: #f8f9fa;
  border-radius: 5px;
  border: 1px solid #dee2e6;
`;

const LegendaTitle = styled.span`
  font-weight: bold;
  color: #495057;
`;

const LegendaItem = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 14px;
  color: #6c757d;
`;

const LegendaColor = styled.div`
  width: 16px;
  height: 16px;
  background-color: ${props => props.color};
  border: 1px solid #dee2e6;
  border-radius: 3px;
`;
